// ============================================================================
// <WorkflowActionBar> — renders available triggers as buttons.
// ============================================================================

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import type { AvailableTrigger } from '../api/types';
import { workflowService } from '../api/workflowService';
import { useWorkflowInstance } from '../hooks/useWorkflowInstance';
import { useWorkflowPermissions } from '../permissions/WorkflowPermissionContext';

interface Props {
  instanceId: string;
  onTriggered?: () => void;
}

const buttonStyle = (allowed: boolean, requiresApproval: boolean): React.CSSProperties => ({
  padding: '0.4rem 0.85rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  border: '1px solid var(--form-border-color)',
  borderRadius: '6px',
  background: requiresApproval ? '#fef3c7' : 'var(--bg-primary)',
  color: requiresApproval ? '#92400e' : 'var(--text-primary)',
  cursor: allowed ? 'pointer' : 'not-allowed',
  opacity: allowed ? 1 : 0.5,
});

export const WorkflowActionBar: React.FC<Props> = ({ instanceId, onTriggered }) => {
  const { data, loading, refetch } = useWorkflowInstance(instanceId);
  const permissions = useWorkflowPermissions();

  const [pending, setPending] = useState<AvailableTrigger | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const handleClick = useCallback((t: AvailableTrigger) => {
    setComment('');
    setPending(t);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const result = await workflowService.trigger(instanceId, pending.triggerCode, {
        comment: comment || undefined,
      });
      if (result.approvalCreated) {
        notificationService.toast.info('Approval requested.');
      } else {
        notificationService.toast.success(`Transition '${pending.triggerCode}' fired.`);
      }
      setPending(null);
      await refetch();
      onTriggered?.();
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Trigger failed.';
      notificationService.toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [pending, comment, instanceId, refetch, onTriggered]);

  if (loading) {
    return <div style={{ padding: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Loading actions…</div>;
  }
  if (!data) return null;
  const triggers = data.availableTriggers;
  if (triggers.length === 0) {
    return <div style={{ padding: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>No actions available from this state.</div>;
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '0.5rem', alignItems: 'center' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Actions:
        </span>
        {triggers.map((t) => {
          const allowed = !t.requiredPermission || permissions.hasPermission(t.requiredPermission);
          return (
            <button
              key={t.transitionId}
              type="button"
              disabled={!allowed || busy}
              style={buttonStyle(allowed, t.requiresApproval)}
              onClick={() => handleClick(t)}
              title={
                allowed
                  ? (t.requiresApproval ? `Requires approval from ${t.approverRoleCode ?? 'an approver'}` : '')
                  : `Missing permission '${t.requiredPermission}'`
              }
            >
              {t.displayName}
              {t.requiresApproval ? ' ⚠' : ''}
            </button>
          );
        })}
      </div>

      {pending && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">
                {pending.requiresApproval ? `Request approval: ${pending.displayName}` : `Confirm: ${pending.displayName}`}
              </h2>
              <button className="modal-close" onClick={() => !busy && setPending(null)} disabled={busy}>
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                {pending.requiresApproval
                  ? `This action requires approval from role '${pending.approverRoleCode}'.`
                  : `Fire trigger '${pending.triggerCode}' and advance to '${pending.toStateCode}'?`}
              </p>
              <label className="form-label" style={{ display: 'block', marginBottom: '0.25rem' }}>Comment (optional)</label>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder="Add a note for the audit trail"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{ width: '100%' }}
                disabled={busy}
              />
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => !busy && setPending(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={handleConfirm} disabled={busy}>
                {busy ? 'Working…' : (pending.requiresApproval ? 'Request approval' : 'Confirm')}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default WorkflowActionBar;
